import type { OverviewSitesRepository } from '../../application/OverviewSitesRepository'
import type {
  OverviewContractCategory,
  OverviewContractType,
  OverviewRequest,
  OverviewRequestStatus,
  OverviewRoute,
  OverviewSite,
  OverviewSitesResult,
  OverviewUnpositioned,
} from '../../domain/overviewSites'

/**
 * Démonstration sans base seulement (mode fixtures), jamais en repli d'une vraie lecture. Toutes les étiquettes
 * commencent par « Démo — » : positions et dossiers fictifs, cohérents avec les maquettes (contrat MLT-DEMO-014 à
 * 30 adresses, 28 sur la carte + 2 à corriger).
 */
const center = { latitude: 45.7804, longitude: -74.0036 }

function rand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

const routes: OverviewRoute[] = [
  { id: 'demo-route-r1', name: 'R1 Nord', color: '#63A7FF', category: 'RESIDENTIAL', sector: 'Nord' },
  { id: 'demo-route-r2', name: 'R2 Centre', color: '#45C987', category: 'RESIDENTIAL', sector: 'Centre' },
  { id: 'demo-route-r3', name: 'R3 Sud', color: '#F1A93A', category: 'RESIDENTIAL', sector: 'Sud' },
  { id: 'demo-route-gs1', name: 'GS-1 Grands sites', color: '#FFD24A', category: 'LARGE_SITE', sector: null },
  { id: 'demo-route-gs2', name: 'GS-2 Grands sites', color: '#FF8A4C', category: 'LARGE_SITE', sector: null },
]

const streets = [
  'Démo-Saint-Georges',
  'Démo-Labelle',
  'Démo-du-Palais',
  'Démo-Parent',
  'Démo-Castonguay',
  'Démo-Brière',
]

function site(
  index: number,
  contract: {
    id: string
    number: string
    client: string
    type: OverviewContractType
    category: OverviewContractCategory
  },
  order: number,
  count: number,
  routeId: string | null,
  offset: { lat: number; lng: number },
): OverviewSite {
  return {
    id: `site:demo-${String(index)}`,
    locationId: `demo-${String(index)}`,
    contractId: contract.id,
    contractNumber: contract.number,
    clientId: null,
    clientNumber: null,
    clientName: contract.client,
    contractType: contract.type,
    contractFlow: contract.type === 'RESIDENTIAL' ? 'LEGACY' : 'VNEXT',
    category: contract.category,
    statusV2: null,
    signedAt: contract.category === 'GREEN' ? '2026-09-15T12:00:00Z' : null,
    season: '2026-2027',
    address: `${String(100 + index * 7)}, rue ${streets[index % streets.length] ?? 'Démo'}`,
    city: 'Saint-Jérôme',
    order,
    contractSiteCount: count,
    latitude: center.latitude + offset.lat,
    longitude: center.longitude + offset.lng,
    positionAdjustedAt: null,
    routeId,
    contractPriceCents:
      contract.type === 'RESIDENTIAL'
        ? 45_000 + (index % 5) * 5_000
        : contract.type === 'MULTIFAMILY'
          ? 3_000_000
          : 450_000,
    locationPriceCents: null,
    parkingSpaces: contract.type === 'RESIDENTIAL' ? null : 12 + (index % 4) * 6,
    drivewayCapacity: contract.type === 'RESIDENTIAL' ? 1 + (index % 5) : null,
    serviceStatus: 'ACTIVE',
  }
}

function buildDemo(): OverviewSitesResult {
  const sites: OverviewSite[] = []
  const categories: OverviewContractCategory[] = [
    'GREEN',
    'GREEN',
    'GREEN',
    'GREEN',
    'YELLOW',
    'ORANGE',
    'EXPIRED',
  ]
  for (let i = 0; i < 70; i += 1) {
    const category = categories[i % categories.length] ?? 'GREEN'
    const routeId = category === 'GREEN' ? (routes[i % 3]?.id ?? null) : null
    sites.push(
      site(
        i,
        {
          id: `demo-res-${String(i)}`,
          number: `RES-DEMO-${String(1000 + i)}`,
          client: `Démo — Résidence ${String(i + 1)}`,
          type: 'RESIDENTIAL',
          category,
        },
        1,
        1,
        routeId,
        { lat: (rand(i) - 0.5) * 0.06, lng: (rand(i + 500) - 0.5) * 0.09 },
      ),
    )
  }
  const mlt = {
    id: 'demo-mlt-014',
    number: 'MLT-DEMO-014',
    client: 'Démo — Habitations Rivière-du-Nord',
    type: 'MULTIFAMILY' as const,
    category: 'GREEN' as const,
  }
  for (let order = 1; order <= 28; order += 1) {
    sites.push(
      site(
        200 + order,
        mlt,
        order,
        30,
        order <= 18 ? 'demo-route-gs1' : order <= 26 ? 'demo-route-gs2' : null,
        {
          lat: (rand(order + 900) - 0.5) * 0.05,
          lng: (rand(order + 1300) - 0.5) * 0.07,
        },
      ),
    )
  }
  const com = {
    id: 'demo-cmr-009',
    number: 'CMR-DEMO-009',
    client: 'Démo — Plaza Laurentides',
    type: 'COMMERCIAL' as const,
    category: 'GREEN' as const,
  }
  for (let order = 1; order <= 3; order += 1) {
    sites.push(
      site(300 + order, com, order, 3, 'demo-route-gs1', { lat: 0.004 * order, lng: -0.006 * order }),
    )
  }
  // Superposition : un ancien Contrat Commercial au même emplacement que la 1re adresse du MLT.
  const first = sites.find((candidate) => candidate.contractId === mlt.id && candidate.order === 1)
  if (first) {
    sites.push({
      ...site(
        400,
        {
          id: 'demo-cmr-031',
          number: 'CMR-DEMO-031',
          client: 'Démo — Ancien gestionnaire',
          type: 'COMMERCIAL',
          category: 'EXPIRED',
        },
        1,
        1,
        null,
        { lat: 0, lng: 0 },
      ),
      latitude: first.latitude,
      longitude: first.longitude,
      address: first.address,
      season: '2025-2026',
    })
  }
  const statuses: OverviewRequestStatus[] = [
    'NEW',
    'NEW',
    'TO_CONTACT',
    'INTERESTED',
    'CONTACTED_NOT_INTERESTED',
    'CONVERTED',
  ]
  const origins = ['groupereca.com', 'Appel entrant', null, 'deneigementstjerome.com']
  const types = [null, 'Multilogements', 'Commercial', 'Résidentiel', 'Déneigement toiture']
  const requests: OverviewRequest[] = []
  for (let i = 0; i < 24; i += 1) {
    const status = statuses[i % statuses.length] ?? 'NEW'
    requests.push({
      id: `lead:demo-${String(i)}`,
      leadId: `demo-lead-${String(i)}`,
      number: `D-DEMO-${String(400 + i)}`,
      name: `Démo — Demande ${String(i + 1)}`,
      address: `${String(10 + i * 3)}, rue Démo-des-Érables`,
      latitude: center.latitude + (rand(i + 2000) - 0.5) * 0.06,
      longitude: center.longitude + (rand(i + 2500) - 0.5) * 0.09,
      status,
      origin: origins[i % origins.length] ?? null,
      serviceType: types[i % types.length] ?? null,
      createdAt: new Date(Date.UTC(2026, 9, 1 + (i % 21))).toISOString(),
      convertedContractId: status === 'CONVERTED' ? 'demo-res-1' : null,
      convertedContractNumber: status === 'CONVERTED' ? 'RES-DEMO-1001' : null,
    })
  }
  const unpositioned: OverviewUnpositioned[] = [29, 30].map((order) => ({
    id: `site:demo-missing-${String(order)}`,
    kind: 'SITE',
    reason: 'NO_COORDINATES',
    label: mlt.client,
    address: `${String(500 + order)}, rue Démo-Fournier`,
    contractId: mlt.id,
    contractNumber: mlt.number,
    contractType: mlt.type,
    category: mlt.category,
    leadId: null,
    leadNumber: null,
  }))
  unpositioned.push({
    id: 'contract:demo-old',
    kind: 'CONTRACT',
    reason: 'NO_SERVICE_ADDRESS',
    label: 'Démo — Ancien contrat sans adresse de service',
    address: null,
    contractId: 'demo-old',
    contractNumber: 'RES-DEMO-0042',
    contractType: 'RESIDENTIAL',
    category: 'GREEN',
    leadId: null,
    leadNumber: null,
  })
  return {
    season: null,
    visible: { contracts: true, requests: true, routes: true },
    sites,
    requests,
    unpositioned,
    routes,
    computedAt: new Date().toISOString(),
    source: 'fixtures',
  }
}

/**
 * Jeu de charge synthétique (essais de lisibilité et de performance de la carte, mandat du 23 oct. 2026) : `?charge=N`
 * ajoute N adresses fictives « Démo — Charge » en grappes denses autour de Saint-Jérôme, dont une part aux mêmes
 * coordonnées, et N/10 demandes. Mode fixtures seulement : jamais injecté dans une base.
 */
export function withCharge(demo: OverviewSitesResult, count: number): OverviewSitesResult {
  if (count <= 0) return demo
  const pockets = [
    { lat: 0, lng: 0, spread: 0.012 },
    { lat: 0.018, lng: -0.02, spread: 0.008 },
    { lat: -0.022, lng: 0.025, spread: 0.01 },
    { lat: 0.035, lng: 0.03, spread: 0.02 },
    { lat: -0.04, lng: -0.05, spread: 0.03 },
  ]
  const categories: OverviewContractCategory[] = ['GREEN', 'GREEN', 'GREEN', 'YELLOW', 'ORANGE', 'EXPIRED']
  const types: OverviewContractType[] = [
    'RESIDENTIAL',
    'RESIDENTIAL',
    'RESIDENTIAL',
    'RESIDENTIAL',
    'COMMERCIAL',
    'MULTIFAMILY',
  ]
  const sites: OverviewSite[] = []
  for (let i = 0; i < count; i += 1) {
    const pocket = pockets[i % pockets.length] ?? { lat: 0, lng: 0, spread: 0.01 }
    const category = categories[i % categories.length] ?? 'GREEN'
    const type = types[i % types.length] ?? 'RESIDENTIAL'
    const generated = site(
      5000 + i,
      {
        id: `demo-charge-${String(i)}`,
        number: `CHG-DEMO-${String(i).padStart(5, '0')}`,
        client: `Démo — Charge ${String(i + 1)}`,
        type,
        category,
      },
      1,
      1,
      category === 'GREEN' ? (routes[i % routes.length]?.id ?? null) : null,
      {
        lat: pocket.lat + (rand(i + 10_000) - 0.5) * pocket.spread,
        lng: pocket.lng + (rand(i + 20_000) - 0.5) * pocket.spread * 1.4,
      },
    )
    const twin = i % 40 === 39 ? sites[i - 1] : undefined
    sites.push(
      twin
        ? { ...generated, latitude: twin.latitude, longitude: twin.longitude, address: twin.address }
        : { ...generated, address: `${String(10 + (i % 990))} rue Démo-Charge-${String(i % 37)}` },
    )
  }
  const requests: OverviewRequest[] = Array.from({ length: Math.floor(count / 10) }, (_, i) => ({
    id: `lead:demo-charge-${String(i)}`,
    leadId: `demo-charge-lead-${String(i)}`,
    number: `D-CHG-${String(i).padStart(4, '0')}`,
    name: `Démo — Demande de charge ${String(i + 1)}`,
    address: `${String(5 + i)} rue Démo-Charge-Demandes`,
    latitude: center.latitude + (rand(i + 30_000) - 0.5) * 0.05,
    longitude: center.longitude + (rand(i + 40_000) - 0.5) * 0.07,
    status: 'NEW',
    origin: null,
    serviceType: null,
    createdAt: new Date(Date.UTC(2026, 9, 1)).toISOString(),
    convertedContractId: null,
    convertedContractNumber: null,
  }))
  return { ...demo, sites: [...demo.sites, ...sites], requests: [...demo.requests, ...requests] }
}

function chargeFromUrl(): number {
  if (typeof window === 'undefined') return 0
  const value = Number.parseInt(new URLSearchParams(window.location.search).get('charge') ?? '', 10)
  return Number.isFinite(value) ? Math.min(Math.max(value, 0), 10_000) : 0
}

export function createFixtureOverviewSitesRepository(): OverviewSitesRepository {
  return {
    load(season) {
      const demo = withCharge(buildDemo(), chargeFromUrl())
      if (!season) return Promise.resolve(demo)
      return Promise.resolve({
        ...demo,
        season,
        sites: demo.sites.filter((candidate) => candidate.season === season),
      })
    },
  }
}
